import { useCallback } from 'react';

import type { EditorActions } from '@atlaskit/editor-core/actions';
import type { Provider } from '@atlaskit/collab-provider/provider';
import type { CollabEditProvider, ResolvedEditorState } from '@atlaskit/editor-common/collab';
import type { GetResolvedEditorStateReason } from '@atlaskit/editor-common/types/editor-actions';

import { flushPendingRemixEdits } from '@atlassian/native-embeds-core/flush-remix-edits';

type UseGetCollabProviderStateProps = {
	editorActions: EditorActions;
	collabEditProvider: (CollabEditProvider & Provider) | null;
	forceAwaitCommitLocalSteps?: boolean;
	reason: GetResolvedEditorStateReason;
};

export const useGetCollabProviderState = ({
	editorActions,
	collabEditProvider,
	forceAwaitCommitLocalSteps = false,
	reason,
}: UseGetCollabProviderStateProps): ((
	awaitCommitLocalSteps?: boolean,
) => Promise<ResolvedEditorState | undefined> | undefined) => {
	// awaitCommitLocalSteps dictates whether we wait until all the user's local steps are committed to collab-service backend before
	// returning the document, or if we return document immediately
	return useCallback(
		(awaitCommitLocalSteps = true) => {
			const getState = () =>
				awaitCommitLocalSteps || !!forceAwaitCommitLocalSteps
					? editorActions.getResolvedEditorState(reason)
					: collabEditProvider?.getCurrentState();
			if (reason === 'publish') {
				const view = editorActions._privateGetEditorView();
				if (view) {
					return flushPendingRemixEdits(view.dom).then(getState);
				}
			}
			return getState();
		},
		[editorActions, collabEditProvider, forceAwaitCommitLocalSteps, reason],
	);
};
